import { useEffect, useRef, useState } from "react";
import MapPicker from "@components/address/MapPicker";
import { useUser, Address } from "@utils/store/user/useUser";
import { appToast } from "@components/Toast";
import { getCurrentLocation, geocodeToAddress } from "@components/address/func/addressTool";
import { createCustomerAddress } from "@screen/QuickOrder/utils/mockApi";
import { findDuplicateAddress } from "@utils/func/addressDedup";

interface ConfirmAddressSheetProps {
  open: boolean;
  onClose: () => void;
  /** 開啟時自動 GPS 定位並帶入地址（QR 新客戶進一鍵下單的自動彈出用）。 */
  autoLocate?: boolean;
  /** 預填值（demo/展示或呼叫端已有定位結果時）。 */
  initial?: { addr?: string; door?: string; floor?: string; lat?: number; lng?: number };
}

const fieldCls =
  "w-full h-[52px] px-4 border-none rounded-[14px] bg-[#F1F5F9] text-[15px] outline-none box-border text-[#333]";

/** 台灣縣市（含台/臺），供自由輸入地址拆結構化欄位（後端 customer_address 為結構化＋generated full_address）。 */
const TW_CITY_RE =
  /^(臺北市|台北市|新北市|基隆市|桃園市|新竹市|新竹縣|苗栗縣|臺中市|台中市|彰化縣|南投縣|雲林縣|嘉義市|嘉義縣|臺南市|台南市|高雄市|屏東縣|宜蘭縣|花蓮縣|臺東縣|台東縣|澎湖縣|金門縣|連江縣)/;

/**
 * Kiosk「確認送達地址」bottom-sheet（沿用 onboarding 填寫配送地址的設計）：
 * 單一地址搜尋框 + 門牌/樓層 + 給配送員指示 + 地圖微調。
 * QR 新客戶進一鍵下單自動彈出：autoLocate 開啟時自動 GPS → 反查地址預填（原切版行為）。
 * 送出走真後端（2026-07-17 接真化，取代舊 demo-gps 佔位）：createCustomerAddress 建立
 * 真實客戶地址並寫入 chooseIdentity（與 AddressPickerSheet 新增邏輯一致），地圖座標一併帶給後端。
 */
const ConfirmAddressSheet = ({ open, onClose, autoLocate, initial }: ConfirmAddressSheetProps) => {
  const { chooseIdentity, setChooseIdentity } = useUser();
  const [addr, setAddr] = useState(initial?.addr || "");
  const [door, setDoor] = useState(initial?.door || "");
  /** MapPicker 定位中 → 卡控送出，避免用到尚未更新的座標 */
  const [isMapGeocoding, setIsMapGeocoding] = useState(false);
  const [floor, setFloor] = useState(initial?.floor || "");
  const [note, setNote] = useState(""); // 給配送員的指示（同 NpAddress，尚未隨地址送後端）
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapForm, setMapForm] = useState<any>({
    latitude: initial?.lat ?? 24.16183,
    longitude: initial?.lng ?? 120.64775,
    city: "",
    area: "",
    road: "",
    streetNumber: "",
    floor: "",
    room: "",
  });
  const mapSetValue = ((name: string, value: any) => setMapForm((f: any) => ({ ...f, [name]: value }))) as any;

  // autoLocate：開啟時自動 GPS 定位 → 反查地址預填（每次開啟只跑一次；失敗留手動輸入）
  const autoRan = useRef(false);
  useEffect(() => {
    if (!open) {
      autoRan.current = false;
      return;
    }
    if (!autoLocate || autoRan.current) return;
    autoRan.current = true;
    (async () => {
      setLocating(true);
      try {
        const res = await getCurrentLocation();
        if (res.state !== "success") {
          appToast.error(res.errorMsg || "定位失敗，請手動輸入地址");
          return;
        }
        const lat = Number(res.lat);
        const lng = Number(res.lng);
        setMapForm((f: any) => ({ ...f, latitude: lat, longitude: lng }));
        const r = await geocodeToAddress(lat, lng);
        if (r) {
          setAddr(`${r.city || ""}${r.area || ""}${r.road || ""}`);
          if (r.streetNumber) setDoor(/號/.test(r.streetNumber) ? r.streetNumber : `${r.streetNumber} 號`);
          appToast.success("已帶入目前位置地址，請確認或修改");
        } else {
          appToast.success("已定位目前位置，請輸入地址並可在地圖微調");
        }
      } finally {
        setLocating(false);
      }
    })();
  }, [open, autoLocate]);

  if (!open) return null;

  const valid = addr.trim().length > 0 && !saving;
  const full = `${addr}${door ? ` ${door}` : ""}${floor ? ` ${floor}` : ""}`.trim();

  const confirm = async () => {
    if (!valid || !chooseIdentity) return;

    // 重複地址：清單已有相同 full_address → 直接選用既有那筆
    const list: Address[] = Array.isArray(chooseIdentity.address) ? (chooseIdentity.address as Address[]) : [];
    const duplicate = findDuplicateAddress(list, full);
    if (duplicate) {
      setChooseIdentity({ ...chooseIdentity, choose_address: duplicate });
      appToast.success("已選用既有的相同地址");
      onClose();
      return;
    }

    // 自由輸入地址拆結構化欄位：拆得出縣市/區就分欄，拆不出整串走 road（後端 full_address 為 generated）
    const trimmed = addr.trim();
    const city = trimmed.match(TW_CITY_RE)?.[1];
    const rest = city ? trimmed.slice(city.length) : trimmed;
    const area = city ? rest.match(/^(\S{1,3}?[區鄉鎮市])/)?.[1] : undefined;
    const road = area ? rest.slice(area.length) : rest;

    setSaving(true);
    try {
      const res = await createCustomerAddress({
        addressInfos: {
          city: city || undefined,
          site_id: area || undefined,
          road: road || undefined,
          address_number: door.trim() || undefined,
          floor: floor.trim() || undefined,
          latitude: mapForm.latitude ?? undefined,
          longitude: mapForm.longitude ?? undefined,
        },
      });
      const newId = res.customerAddressResult.customer_address_id;
      const newAddr: Address = { customer_address_id: newId, full_address: full, city, district: area, floor: floor || undefined };
      setChooseIdentity({ ...chooseIdentity, address: [...list, newAddr], choose_address: newAddr });
      appToast.success("已確認送達地址");
      onClose();
    } catch (e) {
      console.error("ConfirmAddressSheet createCustomerAddress error", e);
      appToast.error("地址儲存失敗，請稍後再試");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[130] flex flex-col justify-end">
      <div className="absolute inset-0 bg-[#0f1a35]/45" onClick={onClose} />
      <div
        className="relative bg-white rounded-t-[24px] px-[20px] pt-2.5 pb-[22px] max-h-[92%] overflow-y-auto"
        style={{ boxShadow: "0 -10px 40px rgba(0,0,0,0.2)" }}
      >
        <div className="w-10 h-[5px] rounded-[3px] bg-[#E2E8F0] mx-auto mb-3" />
        <div className="text-[19px] font-extrabold text-[#0f1a35]">確認送達地址</div>
        <div className="text-[13px] text-[#64748B] mt-1 mb-4">
          {locating ? "正在定位您的位置…" : "已用您目前的位置帶入，請確認或直接修改"}
        </div>

        <div className="text-[13px] text-[#475569] font-semibold mb-2">地址</div>
        <div className="flex items-center gap-2.5 h-[52px] px-[14px] rounded-[14px] bg-[#F1F5F9] mb-3.5">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" stroke="#2196f3" strokeWidth="1.8" />
            <circle cx="12" cy="10" r="2.5" stroke="#2196f3" strokeWidth="1.8" />
          </svg>
          <input
            value={addr}
            onChange={(e) => setAddr(e.target.value)}
            placeholder="搜尋或輸入配送地址"
            className="flex-1 border-none outline-none text-[15px] text-[#333] bg-transparent"
          />
        </div>

        <div className="flex gap-2.5 mb-3.5">
          <div className="flex-1">
            <div className="text-[13px] text-[#475569] font-semibold mb-2">門牌號</div>
            <input value={door} onChange={(e) => setDoor(e.target.value)} placeholder="例：20 號" className={fieldCls} />
          </div>
          <div className="flex-1">
            <div className="text-[13px] text-[#475569] font-semibold mb-2">樓層（選填）</div>
            <input value={floor} onChange={(e) => setFloor(e.target.value)} placeholder="例：5 樓 / B1" className={fieldCls} />
          </div>
        </div>

        <div className="text-[13px] text-[#475569] font-semibold mb-2">給配送員的指示（選填）</div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="例：放門口、按電鈴、有大型犬、樓梯較窄…"
          rows={2}
          className="w-full px-4 py-3 border-none rounded-[14px] bg-[#F1F5F9] text-[15px] leading-[1.5] outline-none box-border text-[#333] resize-none mb-3.5"
        />

        <div className="text-[13px] text-[#475569] font-semibold mb-2">配送位置微調</div>
        <div className="rounded-[14px] overflow-hidden border border-[#E7ECF3] mb-4">
          <MapPicker formFields={mapForm} setValue={mapSetValue} moveHintText="拖曳地圖微調配送位置" onGeocodingChange={setIsMapGeocoding} addressText={addr} />
        </div>

        <button
          onClick={() => void confirm()}
          disabled={!valid || isMapGeocoding}
          className={`w-full py-[15px] rounded-[14px] border-none text-white text-[15px] font-extrabold ${valid && !isMapGeocoding ? "bg-[#2196f3] cursor-pointer" : "bg-[#CBD5E1] cursor-default"}`}
        >
          {isMapGeocoding ? "定位中…" : saving ? "儲存中…" : "確認送達地址"}
        </button>
      </div>
    </div>
  );
};

export default ConfirmAddressSheet;
